import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import ClientBase from "@citrine/client";
import type { MinorStatus } from "@citrine/types/status";

import { BranchClient } from "../branch";
import type { DesignWorkflowExecution } from "../design-workflow-execution";

import type { BranchRoot } from "./branch-root";

interface ListExecutionResponse {
  response: DesignWorkflowExecution[];
}

export class BranchRootClient extends ClientBase<BranchRoot> {
  protected constructor(protected readonly projectId: string) {
    super({
      endpoint: `/api/v2/projects/${projectId}/branch-roots`,
      findField: "response",
      defaultQuery: {
        archived: undefined,
        page: 1,
        // these objects are lightweight, so we can request more than 20 at a time
        per_page: 100,
      },
    });
  }

  static getInstance(projectId: string) {
    return new this(projectId);
  }

  async listExecutions(
    branchRootId: string,
    params?: { archivedExecutions?: boolean; status: MinorStatus }
  ) {
    const result = await this.request<ListExecutionResponse>({
      url: `/api/v3/projects/${this.projectId}/branch-roots/${branchRootId}/executions`,
      params,
    });

    return result.response;
  }

  useListExecutions(
    branchRootId: string,
    params?: { archivedExecutions?: boolean; status: MinorStatus },
    options?: Omit<
      UseQueryOptions<DesignWorkflowExecution[]>,
      "queryKey" | "queryFn"
    >
  ): UseQueryResult<DesignWorkflowExecution[]> {
    return useQuery({
      ...options,
      queryKey: [
        ...this.getModelCacheKey(branchRootId),
        "executions",
        params?.status,
        params?.archivedExecutions,
      ],
      queryFn: () => this.listExecutions(branchRootId, params),
    });
  }

  async archive(branchRootId: string): Promise<BranchRoot> {
    const result = await this.request<BranchRoot>({
      method: "PUT",
      url: `${this.endpoint}/${branchRootId}/archive`,
    });
    this.invalidateAfterMutation(result.id);
    BranchClient.getInstance(this.projectId).invalidateAfterMutation();
    this.addModelToCache(result);
    return result;
  }

  useArchive(
    options: Omit<
      UseMutationOptions<BranchRoot, AxiosError, string>,
      "mutationFn"
    > = {}
  ): UseMutationResult<BranchRoot, AxiosError, string> {
    return useMutation<BranchRoot, AxiosError, string>({
      ...options,
      mutationFn: (branchRootId) => this.archive(branchRootId),
    });
  }

  async restore(branchRootId: string): Promise<BranchRoot> {
    const result = await this.request<BranchRoot>({
      method: "PUT",
      url: `${this.endpoint}/${branchRootId}/restore`,
    });
    this.invalidateAfterMutation(result.id);
    BranchClient.getInstance(this.projectId).invalidateAfterMutation();
    this.addModelToCache(result);
    return result;
  }

  useRestore(
    options: Omit<
      UseMutationOptions<BranchRoot, AxiosError, string>,
      "mutationFn"
    > = {}
  ) {
    return useMutation<BranchRoot, AxiosError, string>({
      ...options,
      mutationFn: (branchRootId) => this.restore(branchRootId),
    });
  }

  protected fromModel(model: Partial<BranchRoot>) {
    return model.data;
  }
}
